import { useEffect, useState } from 'react';
import { useMediaQuery, Typography, AccordionDetails, AccordionSummary, Accordion, Box } from '@mui/material';
import { DeliveryType, OrderStatus } from 'orderme-api-integration-client';
import { useTranslation } from 'react-i18next';
import { getOrderStatusStep, getStepLabel, OrderStatusSteps } from '../../helpers/orderStatus';
import StepperVertical from './stepperVertical';
import StepperHorizontal from './stepperHorizontal';

interface OrderStatusStepperProps {
  status: OrderStatus;
  isOnlyPhysicalItems: boolean;
  deliveryType: DeliveryType;
}

const getPhysicalItemsSteps = (deliveryType: DeliveryType) => {
  const stepLabels = [OrderStatusSteps.Created, OrderStatusSteps.Processing, OrderStatusSteps.PickedUpByCourier];
  if (deliveryType === DeliveryType.SalesPoint) {
    stepLabels.push(OrderStatusSteps.Ready);
    stepLabels.push(OrderStatusSteps.Completed);
  }
  return stepLabels;
};

const getServicesSteps = (deliveryType: DeliveryType) => {
  const stepLabels: OrderStatusSteps[] = [
    OrderStatusSteps.Created,
    OrderStatusSteps.Accepted,
    OrderStatusSteps.Processing,
  ];

  if (deliveryType === DeliveryType.Address) {
    stepLabels.push(OrderStatusSteps.PickedUpByCourier);
  } else {
    stepLabels.push(OrderStatusSteps.Ready);
    stepLabels.push(OrderStatusSteps.Completed);
  }

  return stepLabels;
};

const getSteps = (deliveryType: DeliveryType, isOnlyPhysicalItems: boolean) => {
  return isOnlyPhysicalItems ? getPhysicalItemsSteps(deliveryType) : getServicesSteps(deliveryType);
};

const isPaid = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.WaitingForPayment:
    case OrderStatus.ProcessingPayment:
    case OrderStatus.Cancelled:
      return false;
    default:
      return true;
  }
};

export function OrderStatusStepper(props: OrderStatusStepperProps) {
  const [allSteps, setAllSteps] = useState<OrderStatusSteps[]>([]);
  const [activeStep, setActiveStep] = useState<OrderStatusSteps>(OrderStatusSteps.None);
  const [allStepLabels, setAllStepLabels] = useState<string[]>();
  const [activeStepIndex, setActiveStepIndex] = useState<number>(-1);
  const [expandedStepper, setExpandedStepper] = useState<boolean>(false);
  const { t } = useTranslation();
  const showStepLabel = useMediaQuery('(min-width:800px)');

  useEffect(() => {
    setAllSteps(getSteps(props.deliveryType, props.isOnlyPhysicalItems));
    setActiveStep(getOrderStatusStep(props.status, props.isOnlyPhysicalItems, props.deliveryType));
  }, [props]);

  useEffect(() => {
    setAllStepLabels(allSteps.map((step) => getStepLabel(step, props.isOnlyPhysicalItems)));
    setActiveStepIndex(allSteps.findIndex((step) => step === activeStep));
  }, [allSteps, activeStep, props.isOnlyPhysicalItems]);

  useEffect(() => {
    if (showStepLabel) {
      setExpandedStepper(false);
    }
  }, [showStepLabel]);

  const getActiveStatusLabel = () => {
    if (!allStepLabels?.length) {
      return;
    }

    if (isPaid(props.status)) {
      return allStepLabels[activeStepIndex];
    }

    if (props.status === OrderStatus.ProcessingPayment) {
      return t('orderStatus:processingPayment');
    }
  };

  return (
    allSteps?.length > 0 && (
      <Accordion
        sx={{ mt: '10px !important', p: 0 }}
        expanded={expandedStepper}
        onChange={(_, expanded) => setExpandedStepper(expanded && !showStepLabel)}
      >
        <AccordionSummary
          sx={{
            p: 0,
            cursor: showStepLabel && 'default !important',
            '.MuiAccordionSummary-content': {
              margin: 0,
            },
          }}
        >
          <Box sx={{ width: '100%' }}>
            <StepperHorizontal
              steps={allStepLabels}
              currentStep={activeStepIndex}
              isError={props.status === OrderStatus.Cancelled}
              showStepLabel={showStepLabel}
            />
            {!showStepLabel && props.status !== OrderStatus.Cancelled && (
              <Box>
                <Typography sx={{ mt: '3px', textAlign: 'center' }}>{getActiveStatusLabel()}</Typography>
              </Box>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <StepperVertical
            steps={allStepLabels}
            currentStep={activeStepIndex}
            isError={props.status === OrderStatus.Cancelled}
          />
        </AccordionDetails>
      </Accordion>
    )
  );
}

export default OrderStatusStepper;
